import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Search, Edit2, Trash2, Eye, EyeOff, RefreshCw,
  Package, Save, X, Image, Euro, Tag, Truck,
  AlertCircle, CheckCircle, Clock, BarChart2, Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { fileToResizedDataUrl } from '../utils/imageUpload';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TVA_OPTIONS = [
  { value: 20, label: '20% (standard)' },
  { value: 10, label: '10% (restauration)' },
  { value: 5.5, label: '5.5% (alimentaire)' },
  { value: 0, label: '0% (exonéré)' },
];

const STOCK_STATUS = {
  en_stock:    { label: 'En stock ✅', color: 'var(--admin-green)', bg: 'rgba(31,157,122,0.1)' },
  sur_commande:{ label: 'Sur commande ⏳', color: 'var(--admin-amber)', bg: 'rgba(197,139,25,0.1)' },
  rupture:     { label: 'Rupture ❌', color: 'var(--admin-red)', bg: 'rgba(229,62,62,0.1)' },
};

const EMPTY = {
  nom: '', description_courte: '', description_longue: '',
  prix_ht: '', taux_tva: 20, images: [''],
  stock: '', stock_status: 'en_stock', delai_expedition_jours: 4,
  poids_kg: '', transporteurs: ['mondial_relay','chronopost','colissimo'],
  tags: '', sku: '', categorie_id: '', disponible: true, featured: false,
  seo: { seo_title:'', meta_description:'', slug:'', keywords:[], og_title:'', og_description:'' },
  faq: [], conseils_utilisation: '', variantes: [],
};

function ProductVenteForm({ product, categories, onSave, onCancel }) {
  const [form, setForm] = useState(() => product ? {
    ...product,
    tags: Array.isArray(product.tags) ? product.tags.join(', ') : product.tags || '',
    images: product.images?.length ? product.images : [''],
  } : { ...EMPTY });
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const prixHT = parseFloat(form.prix_ht) || 0;
  const tva    = parseFloat(form.taux_tva) || 20;
  const prixTTC = Math.round(prixHT * (1 + tva / 100) * 100) / 100;

  function setF(k, v) { setForm(p => ({ ...p, [k]: v })); }
  function setSEO(k, v) { setForm(p => ({ ...p, seo: { ...(p.seo||{}), [k]: v } })); }

  async function submit() {
    if (!form.nom.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        tags: typeof form.tags === 'string' ? form.tags.split(',').map(t=>t.trim()).filter(Boolean) : form.tags,
        images: (form.images||['']).filter(Boolean),
        prix_ht: parseFloat(form.prix_ht)||0,
        prix_ttc: prixTTC,
        stock: form.stock !== '' ? parseInt(form.stock) : null,
        poids_kg: form.poids_kg !== '' ? parseFloat(form.poids_kg) : null,
        delai_expedition_jours: parseInt(form.delai_expedition_jours)||4,
      };
      const url = product ? `${BACKEND}/api/admin/catalogue/vente/${product.id}` : `${BACKEND}/api/admin/catalogue/vente`;
      const method = product ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: H, body: JSON.stringify(body) });
      if (!res.ok) { const e=await res.json(); throw new Error(e.detail); }
      toast.success(product ? 'Produit mis à jour !' : 'Produit créé !');
      onSave();
    } catch(err) { toast.error(err.message||'Erreur'); }
    setSaving(false);
  }

  const TABS = [
    { id:'general', label:'Général' },
    { id:'prix', label:'Prix & Stock' },
    { id:'images', label:'Photos' },
    { id:'seo', label:'SEO' },
    { id:'logistique', label:'Logistique' },
    { id:'faq', label:'FAQ' },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ backdropFilter:'blur(6px)', background:'rgba(0,0,0,0.8)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="w-full max-w-3xl rounded-2xl" style={{ background:'#0D0D0D', border:'1px solid rgba(255,255,255,0.1)' }}>
          {/* Header */}
          <div className="flex items-center justify-between p-5" style={{ borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
            <h2 className="font-bold text-lg" style={{ color:'#5DBCBF' }}>{product ? '✏️ Modifier' : '➕ Nouveau'} produit vente</h2>
            <button onClick={onCancel} style={{ color:'rgba(255,255,255,0.4)' }}><X size={20}/></button>
          </div>

          {/* Tabs */}
          <div className="flex overflow-x-auto" style={{ borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="px-4 py-3 text-sm font-medium flex-shrink-0"
                style={activeTab===tab.id ? { color:'#5DBCBF', borderBottom:'2px solid #5DBCBF' } : { color:'rgba(255,255,255,0.4)' }}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-5 space-y-4" style={{ maxHeight:'60vh', overflowY:'auto' }}>
            {activeTab==='general' && (
              <>
                <Field label="Nom du produit *" value={form.nom} onChange={e=>setF('nom',e.target.value)} placeholder="Ex: Arche bois hexagonale" />
                <Field label="SKU / Référence" value={form.sku} onChange={e=>setF('sku',e.target.value)} placeholder="SE-001" />
                <div>
                  <label className="text-xs mb-1 block" style={{ color:'rgba(255,255,255,0.5)' }}>Description courte (affichée en liste)</label>
                  <textarea value={form.description_courte} onChange={e=>setF('description_courte',e.target.value)}
                    rows={2} placeholder="Description commerciale courte..."
                    className="w-full px-3 py-2 rounded-xl text-sm resize-y"
                    style={{ background:'#1a1a1a', border:'1px solid rgba(255,255,255,0.1)', color:'#fff', outline:'none' }} />
                </div>
                <div>
                  <label className="text-xs mb-1 block" style={{ color:'rgba(255,255,255,0.5)' }}>Description longue SEO</label>
                  <textarea value={form.description_longue} onChange={e=>setF('description_longue',e.target.value)}
                    rows={4} placeholder="Description complète optimisée pour les moteurs de recherche..."
                    className="w-full px-3 py-2 rounded-xl text-sm resize-y"
                    style={{ background:'#1a1a1a', border:'1px solid rgba(255,255,255,0.1)', color:'#fff', outline:'none' }} />
                </div>
                <div>
                  <label className="text-xs mb-1 block" style={{ color:'rgba(255,255,255,0.5)' }}>Conseils d'utilisation</label>
                  <textarea value={form.conseils_utilisation} onChange={e=>setF('conseils_utilisation',e.target.value)}
                    rows={2} placeholder="Comment utiliser ce produit..."
                    className="w-full px-3 py-2 rounded-xl text-sm resize-y"
                    style={{ background:'#1a1a1a', border:'1px solid rgba(255,255,255,0.1)', color:'#fff', outline:'none' }} />
                </div>
                <Field label="Tags (séparés par virgule)" value={form.tags} onChange={e=>setF('tags',e.target.value)} placeholder="mariage, anniversaire, déco" />
              </>
            )}

            {activeTab==='prix' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs mb-1 block" style={{ color:'rgba(255,255,255,0.5)' }}>Prix HT (€) *</label>
                    <Input type="number" min="0" step="0.01" value={form.prix_ht} onChange={e=>setF('prix_ht',e.target.value)}
                      style={{ background:'#1a1a1a', borderColor:'rgba(255,255,255,0.1)', color:'#fff' }} />
                  </div>
                  <div>
                    <label className="text-xs mb-1 block" style={{ color:'rgba(255,255,255,0.5)' }}>TVA</label>
                    <select value={form.taux_tva} onChange={e=>setF('taux_tva',parseFloat(e.target.value))}
                      className="w-full px-3 py-2 rounded-xl text-sm" style={{ background:'#1a1a1a', border:'1px solid rgba(255,255,255,0.1)', color:'#fff' }}>
                      {TVA_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
                {prixHT > 0 && (
                  <div className="p-3 rounded-xl" style={{ background:'rgba(212,175,55,0.08)', border:'1px solid rgba(212,175,55,0.2)' }}>
                    <p className="text-lg font-bold" style={{ color:'#D4AF37' }}>Prix TTC = {prixTTC.toFixed(2)}€</p>
                    <p className="text-xs" style={{ color:'rgba(255,255,255,0.4)' }}>TVA : {(prixTTC-prixHT).toFixed(2)}€ ({tva}%)</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs mb-1 block" style={{ color:'rgba(255,255,255,0.5)' }}>Stock (vide = illimité)</label>
                    <Input type="number" min="0" value={form.stock} onChange={e=>setF('stock',e.target.value)}
                      placeholder="Ex: 50" style={{ background:'#1a1a1a', borderColor:'rgba(255,255,255,0.1)', color:'#fff' }} />
                  </div>
                  <div>
                    <label className="text-xs mb-1 block" style={{ color:'rgba(255,255,255,0.5)' }}>Statut stock</label>
                    <select value={form.stock_status} onChange={e=>setF('stock_status',e.target.value)}
                      className="w-full px-3 py-2 rounded-xl text-sm" style={{ background:'#1a1a1a', border:'1px solid rgba(255,255,255,0.1)', color:'#fff' }}>
                      {Object.entries(STOCK_STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="p-3 rounded-xl" style={{ background:`${STOCK_STATUS[form.stock_status]?.bg||'rgba(0,0,0,0.1)'}`, border:`1px solid ${STOCK_STATUS[form.stock_status]?.color||'rgba(255,255,255,0.1)'}25` }}>
                  <p className="text-xs font-semibold" style={{ color:STOCK_STATUS[form.stock_status]?.color }}>
                    {form.stock_status==='sur_commande'
                      ? `⏳ Ce produit est préparé sur commande. Délai minimum avant expédition : ${form.delai_expedition_jours} jours ouvrés.`
                      : form.stock_status==='rupture'
                      ? '❌ Produit en rupture de stock. Non commandable.'
                      : '✅ Produit en stock. Expédition sous 24-48h.'}
                  </p>
                </div>
                <Field label="Délai min. expédition (jours ouvrés)" type="number" value={form.delai_expedition_jours}
                  onChange={e=>setF('delai_expedition_jours',e.target.value)} />
              </>
            )}

            {activeTab==='images' && (
              <>
                <p className="text-xs" style={{ color:'rgba(255,255,255,0.4)' }}>Ajoutez jusqu'à 8 URLs d'images. La première est l'image principale.</p>
                {(form.images||['']).map((img,i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <span className="text-xs" style={{ color:'rgba(255,255,255,0.3)', width:20 }}>{i+1}</span>
                    <Input value={img} onChange={e=>{ const imgs=[...(form.images||[''])]; imgs[i]=e.target.value; setF('images',imgs); }}
                      placeholder={`URL image ${i+1}...`} style={{ background:'#1a1a1a', borderColor:'rgba(255,255,255,0.1)', color:'#fff', flex:1 }} />
                    {img && <img src={img} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" onError={e=>e.target.style.display='none'} />}
                    <button onClick={()=>setF('images',(form.images||['']).filter((_,j)=>j!==i))} style={{ color:'#e53e3e' }}><X size={14}/></button>
                  </div>
                ))}
                {(form.images||['']).length < 8 && (
                  <button onClick={()=>setF('images',[...(form.images||['']),""])}
                    className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg"
                    style={{ background:'rgba(93,188,191,0.1)', color:'#5DBCBF', border:'1px solid rgba(93,188,191,0.2)' }}>
                    <Plus size={12}/> Ajouter une URL
                  </button>
                )}
                <label className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg cursor-pointer w-fit"
                  data-testid="upload-image-vente"
                  style={{ background:'rgba(212,175,55,0.1)', color:'#D4AF37', border:'1px solid rgba(212,175,55,0.2)' }}>
                  <Upload size={12}/> Importer depuis mon ordinateur
                  <input type="file" accept="image/*" style={{ display:'none' }} onChange={async (e)=>{
                    const file = e.target.files?.[0]; e.target.value='';
                    if (!file) return;
                    if (file.size > 8 * 1024 * 1024) { toast.error('Image trop lourde (max 8 Mo)'); return; }
                    try {
                      const dataUrl = await fileToResizedDataUrl(file);
                      setF('images',[...(form.images||[]).filter(Boolean), dataUrl]);
                      toast.success('Photo importée');
                    } catch (err) { toast.error(err.message || 'Import impossible'); }
                  }} />
                </label>
              </>
            )}

            {activeTab==='seo' && (
              <>
                <Field label="Titre SEO (balise <title>)" value={form.seo?.seo_title||''} onChange={e=>setSEO('seo_title',e.target.value)} placeholder={form.nom} />
                <Field label="Meta titre" value={form.seo?.meta_title||''} onChange={e=>setSEO('meta_title',e.target.value)} placeholder={form.nom} />
                <div>
                  <label className="text-xs mb-1 block" style={{ color:'rgba(255,255,255,0.5)' }}>Meta description (160 caractères max)</label>
                  <textarea value={form.seo?.meta_description||''} onChange={e=>setSEO('meta_description',e.target.value)}
                    rows={2} maxLength={160} placeholder="Description pour les moteurs de recherche..."
                    className="w-full px-3 py-2 rounded-xl text-sm resize-y"
                    style={{ background:'#1a1a1a', border:'1px solid rgba(255,255,255,0.1)', color:'#fff', outline:'none' }} />
                  <p className="text-xs mt-0.5" style={{ color:'rgba(255,255,255,0.3)' }}>{(form.seo?.meta_description||'').length}/160</p>
                </div>
                <Field label="Slug URL (ex: arche-bois-hexagonale)" value={form.seo?.slug||''} onChange={e=>setSEO('slug',e.target.value)}
                  placeholder={form.nom?.toLowerCase().replace(/ /g,'-')||''} />
                <Field label="Mots-clés (virgule séparateur)" value={(form.seo?.keywords||[]).join(', ')}
                  onChange={e=>setSEO('keywords',e.target.value.split(',').map(k=>k.trim()).filter(Boolean))} />
                <Field label="Open Graph titre" value={form.seo?.og_title||''} onChange={e=>setSEO('og_title',e.target.value)} />
                <Field label="Open Graph description" value={form.seo?.og_description||''} onChange={e=>setSEO('og_description',e.target.value)} />
                <Field label="URL canonique" value={form.seo?.canonical_url||''} onChange={e=>setSEO('canonical_url',e.target.value)} />
                <div className="p-3 rounded-xl" style={{ background:'rgba(93,188,191,0.05)', border:'1px solid rgba(93,188,191,0.15)' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color:'#5DBCBF' }}>Données structurées Google (JSON-LD)</p>
                  <p className="text-xs" style={{ color:'rgba(255,255,255,0.4)' }}>
                    Générées automatiquement au format Product schema.org. Vérifiez sur search.google.com/test/rich-results
                  </p>
                </div>
              </>
            )}

            {activeTab==='logistique' && (
              <>
                <Field label="Poids (kg)" type="number" step="0.1" value={form.poids_kg} onChange={e=>setF('poids_kg',e.target.value)} placeholder="0.5" />
                <p className="text-xs font-semibold" style={{ color:'rgba(255,255,255,0.5)' }}>Transporteurs activés</p>
                {['mondial_relay','chronopost','colissimo'].map(t => (
                  <label key={t} className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox"
                      checked={(form.transporteurs||[]).includes(t)}
                      onChange={e=>setF('transporteurs', e.target.checked ? [...(form.transporteurs||[]),t] : (form.transporteurs||[]).filter(x=>x!==t))}
                      style={{ accentColor:'#5DBCBF' }}/>
                    <span className="text-sm capitalize" style={{ color:'#fff' }}>{t.replace(/_/g,' ')}</span>
                  </label>
                ))}
                <div className="p-3 rounded-xl" style={{ background:'rgba(197,139,25,0.08)', border:'1px solid rgba(197,139,25,0.2)' }}>
                  <p className="text-xs font-semibold" style={{ color:'#C58B19' }}>Note : APIs transporteurs non connectées</p>
                  <p className="text-xs" style={{ color:'rgba(255,255,255,0.4)' }}>Les frais de livraison seront calculés via estimation tarifaire jusqu'à la connexion des vraies APIs Mondial Relay et Chronopost.</p>
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.fragile||false} onChange={e=>setF('fragile',e.target.checked)} style={{ accentColor:'#e53e3e' }}/>
                  <span className="text-sm" style={{ color:'#fff' }}>Produit fragile (mention spéciale)</span>
                </label>
              </>
            )}

            {activeTab==='faq' && (
              <>
                {(form.faq||[]).map((item,i) => (
                  <div key={i} className="p-3 rounded-xl space-y-2" style={{ background:'var(--admin-surface)', border:'1px solid rgba(255,255,255,0.06)' }}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold" style={{ color:'#D4AF37' }}>Question {i+1}</p>
                      <button onClick={()=>setF('faq',(form.faq||[]).filter((_,j)=>j!==i))} style={{ color:'#e53e3e' }}><X size={13}/></button>
                    </div>
                    <Input value={item.question} onChange={e=>{ const faq=[...(form.faq||[])]; faq[i]={...faq[i],question:e.target.value}; setF('faq',faq); }}
                      placeholder="Question client..." style={{ background:'#222', borderColor:'rgba(255,255,255,0.1)', color:'#fff' }} />
                    <textarea value={item.reponse} onChange={e=>{ const faq=[...(form.faq||[])]; faq[i]={...faq[i],reponse:e.target.value}; setF('faq',faq); }}
                      rows={2} placeholder="Réponse..." className="w-full px-3 py-2 rounded-xl text-sm resize-y"
                      style={{ background:'#222', border:'1px solid rgba(255,255,255,0.1)', color:'#fff', outline:'none' }} />
                  </div>
                ))}
                <button onClick={()=>setF('faq',[...(form.faq||[]),{question:'',reponse:''}])}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg"
                  style={{ background:'rgba(212,175,55,0.1)', color:'#D4AF37', border:'1px solid rgba(212,175,55,0.2)' }}>
                  <Plus size={12}/> Ajouter une question FAQ
                </button>
              </>
            )}
          </div>

          <div className="flex items-center gap-4 px-5 py-4" style={{ borderTop:'1px solid rgba(255,255,255,0.06)' }}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.disponible} onChange={e=>setF('disponible',e.target.checked)} style={{ accentColor:'#1F9D7A' }}/>
              <span className="text-xs" style={{ color:'#fff' }}>Visible sur le site</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={e=>setF('featured',e.target.checked)} style={{ accentColor:'#D4AF37' }}/>
              <span className="text-xs" style={{ color:'#fff' }}>Bestseller</span>
            </label>
            <div className="flex-1" />
            <Button onClick={submit} disabled={saving} style={{ background:'rgba(93,188,191,0.2)', color:'#5DBCBF', border:'1px solid rgba(93,188,191,0.3)' }}>
              <Save size={14} className="mr-2"/>{saving ? 'Sauvegarde...' : (product ? 'Mettre à jour' : 'Créer')}
            </Button>
            <Button onClick={onCancel} variant="outline" style={{ borderColor:'rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.5)' }}>Annuler</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type='text', ...rest }) {
  return (
    <div>
      <label className="text-xs mb-1 block" style={{ color:'rgba(255,255,255,0.5)' }}>{label}</label>
      <Input type={type} placeholder={placeholder||''} value={value||''} onChange={onChange} {...rest}
        style={{ background:'#1a1a1a', borderColor:'rgba(255,255,255,0.1)', color:'#fff' }} />
    </div>
  );
}

export default function AdminCatalogueVente() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type':'application/json', Authorization:`Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${BACKEND}/api/admin/catalogue/vente?limit=100`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (stockFilter) url += `&stock_status=${stockFilter}`;
      const d = await fetch(url, { headers: H }).then(r => r.json());
      setProducts(d.products||[]); setTotal(d.total||0);
    } catch { toast.error('Erreur'); }
    setLoading(false);
  // eslint-disable-next-line
  }, [search, stockFilter]);

  useEffect(() => { load(); }, [load]);

  async function toggleDispo(p) {
    await fetch(`${BACKEND}/api/admin/catalogue/vente/${p.id}`, {
      method:'PUT', headers:H, body:JSON.stringify({disponible:!p.disponible})
    });
    load();
  }

  async function del(p) {
    if (!window.confirm(`Supprimer "${p.nom}" ?`)) return;
    await fetch(`${BACKEND}/api/admin/catalogue/vente/${p.id}`, { method:'DELETE', headers:H });
    toast.success('Supprimé'); load();
  }

  async function moveToType(p, newType) {
    if (!newType) return;
    await fetch(`${BACKEND}/api/products/${p.id}`, { method:'PUT', headers:H, body: JSON.stringify({ type: newType }) });
    toast.success(`« ${p.nom} » déplacé`); load();
  }

  return (
    <div data-testid="admin-catalogue-vente">
      {/* Header cockpit */}
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Produits</p>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, marginTop:4 }}>
          <div>
            <h2 style={{ fontFamily:'var(--admin-font-display)', fontSize:26, fontWeight:700, letterSpacing:'-.6px', color:'var(--admin-text)' }}>
              Produits à vendre
            </h2>
            <p style={{ color:'var(--admin-muted)', fontSize:13.5, marginTop:3 }}>
              {total} produit{total>1?'s':''} · Boutique en ligne
            </p>
          </div>
          <div style={{ display:'flex', gap:7 }}>
            <button onClick={load}
              style={{ width:34, height:34, borderRadius:10, border:'1px solid var(--admin-border)',
                background:'var(--admin-surface)', cursor:'pointer', display:'flex', alignItems:'center',
                justifyContent:'center', color:'var(--admin-muted)' }}>
              <RefreshCw size={13}/>
            </button>
            <button onClick={() => { setEditProduct(null); setShowForm(true); }}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:10,
                background:'var(--admin-green)', color:'#052b25', border:'none',
                fontSize:13, fontWeight:700, cursor:'pointer' }}>
              <Plus size={14}/> Nouveau produit
            </button>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
        <div style={{ position:'relative', flex:1, minWidth:180 }}>
          <Search size={13} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:'var(--admin-faint)' }}/>
          <input placeholder="Rechercher produit..." value={search} onChange={e=>setSearch(e.target.value)}
            style={{ width:'100%', padding:'9px 14px 9px 32px', borderRadius:10, boxSizing:'border-box',
              background:'var(--admin-surface)', border:'1px solid var(--admin-border-soft)',
              color:'var(--admin-text)', fontSize:13.5, outline:'none' }}/>
        </div>
        <select value={stockFilter} onChange={e=>setStockFilter(e.target.value)}
          style={{ padding:'9px 12px', borderRadius:10, background:'var(--admin-surface)',
            border:'1px solid var(--admin-border-soft)', color:'var(--admin-text)', fontSize:13, outline:'none' }}>
          <option value="">Tous les stocks</option>
          {Object.entries(STOCK_STATUS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Liste produits */}
      {loading ? (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {[1,2,3,4].map(i => <div key={i} className="adm-card" style={{ height:68, background:'var(--admin-surface2)' }}/>)}
        </div>
      ) : products.length === 0 ? (
        <div style={{ textAlign:'center', padding:'50px 20px', color:'var(--admin-faint)' }}>
          <Package size={36} style={{ margin:'0 auto 12px', opacity:.2 }}/>
          <p style={{ fontSize:14 }}>Aucun produit de vente</p>
          <p style={{ fontSize:12, marginTop:6 }}>Ajoutez vos premiers produits pour la boutique.</p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
          {products.map(p => {
            const ss = STOCK_STATUS[p.stock_status] || STOCK_STATUS.en_stock;
            return (
              <div key={p.id} className="adm-card"
                style={{ padding:'12px 14px', display:'flex', alignItems:'center', gap:12,
                  opacity: p.disponible ? 1 : 0.6 }}>
                {/* Image */}
                {p.images?.[0] ? (
                  <img src={p.images[0]} alt={p.nom} loading="lazy"
                    style={{ width:48, height:48, borderRadius:11, objectFit:'cover', flexShrink:0 }}/>
                ) : (
                  <div style={{ width:48, height:48, borderRadius:11, flexShrink:0,
                    background:'var(--admin-surface2)', display:'flex', alignItems:'center',
                    justifyContent:'center', color:'var(--admin-faint)', fontSize:18 }}>🛒</div>
                )}
                {/* Infos */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:7, flexWrap:'wrap', marginBottom:3 }}>
                    <p style={{ fontSize:14, fontWeight:600, color:'var(--admin-text)', margin:0 }}>{p.nom}</p>
                    {/* Pastille stock */}
                    <span style={{ fontSize:10.5, fontWeight:700, padding:'2px 8px', borderRadius:7,
                      background: ss.bg, color: ss.color }}>
                      {ss.label}{p.stock!=null ? ` · ${p.stock}` : ''}
                    </span>
                    {!p.disponible && (
                      <span style={{ fontSize:10.5, fontWeight:700, padding:'2px 8px', borderRadius:7,
                        background:'var(--admin-red-dim)', color:'var(--admin-red)' }}>Masqué</span>
                    )}
                  </div>
                  <p style={{ fontSize:12, color:'var(--admin-muted)', margin:0 }}>
                    {p.sku ? `${p.sku} · ` : ''}
                    HT : {p.prix_ht||0}€ · TTC : <strong style={{ color:'var(--admin-text)' }}>{p.prix_ttc||0}€</strong>
                    {p.delai_expedition_jours ? ` · Délai ${p.delai_expedition_jours}j` : ''}
                  </p>
                </div>
                {/* Actions */}
                <div style={{ display:'flex', gap:5, flexShrink:0, alignItems:'center' }}>
                  <select value="" onChange={e => moveToType(p, e.target.value)} title="Déplacer vers un autre univers"
                    data-testid={`vente-move-${p.id}`}
                    style={{ height:32, borderRadius:9, border:'1px solid var(--admin-border)', cursor:'pointer',
                      background:'var(--admin-surface2)', color:'var(--admin-muted)', fontSize:11, padding:'0 6px' }}>
                    <option value="">Déplacer…</option>
                    <option value="prestation">Prestation</option>
                    <option value="location">Location</option>
                  </select>
                  <button onClick={() => toggleDispo(p)} title={p.disponible ? 'Masquer' : 'Rendre visible'}
                    style={{ width:32, height:32, borderRadius:9, border:'none', cursor:'pointer',
                      background: p.disponible ? 'var(--admin-green-dim)' : 'var(--admin-surface2)',
                      color: p.disponible ? 'var(--admin-green)' : 'var(--admin-faint)',
                      display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {p.disponible ? <Eye size={13}/> : <EyeOff size={13}/>}
                  </button>
                  <button onClick={() => { setEditProduct(p); setShowForm(true); }} title="Modifier"
                    style={{ width:32, height:32, borderRadius:9, border:'none', cursor:'pointer',
                      background:'var(--admin-blue-dim)', color:'var(--admin-blue)',
                      display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <Edit2 size={13}/>
                  </button>
                  <button onClick={() => del(p)} title="Supprimer"
                    style={{ width:32, height:32, borderRadius:9, border:'none', cursor:'pointer',
                      background:'var(--admin-red-dim)', color:'var(--admin-red)',
                      display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <Trash2 size={13}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Formulaire édition — INTACT */}
      {showForm && (
        <ProductVenteForm
          product={editProduct}
          categories={[]}
          onSave={() => { setShowForm(false); setEditProduct(null); load(); }}
          onCancel={() => { setShowForm(false); setEditProduct(null); }}
        />
      )}
    </div>
  );
}
